import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useFinance, usePreferences } from '../../../contexts';
import { getScaleColor, SERIES_COLOR } from '../colorScale';
import styles from './ComparativoMensal.module.css';

interface TooltipProps {
    active?: boolean;
    payload?: Array<{ value: number; name: string; color: string }>;
    label?: string;
    formatCurrency: (value: number) => string;
}

const CustomTooltip = ({ active, payload, label, formatCurrency }: TooltipProps) => {
    if (active && payload?.length) {
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{label}</p>
                {payload.map((entry, index) => (
                    <p key={index} style={{ color: entry.color }}>
                        {entry.name}:{' '}
                        {formatCurrency(entry.value)}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

export function ComparativoMensal() {
    const { cashFlowData } = useFinance();
    const { formatCurrency, t } = usePreferences();

    const maxIncome = Math.max(...cashFlowData.map(d => d.income), 1);
    const maxExpense = Math.max(...cashFlowData.map(d => d.expense), 1);

    const formatYAxis = (value: number) => `${(value / 1000).toFixed(0)}k`;

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{t('chart.comparativoMensal')}</h3>
            <span className={styles.subtitle}>{t('chart.receitasVsDespesas')}</span>

            <div className={styles.chartContainer}>
                {cashFlowData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={cashFlowData}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12, fill: '#9ca3af' }}
                        />
                        <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                        <Legend
                            verticalAlign="top"
                            align="left"
                            height={36}
                            iconType="circle"
                            iconSize={8}
                            formatter={(value) => (
                                <span style={{ color: '#6b7280', fontSize: '12px' }}>{value}</span>
                            )}
                        />
                        {/*
                          * O fill da série é o que a legenda e o tooltip leem; as Cells
                          * abaixo só pintam cada barra dentro da escala. Sem ele o ícone
                          * da legenda sai preto.
                          */}
                        <Bar
                            dataKey="income"
                            name={t('chart.receitas')}
                            fill={SERIES_COLOR.income}
                            radius={[4, 4, 0, 0]}
                            animationDuration={1500}
                        >
                            {cashFlowData.map((entry, index) => (
                                <Cell key={index} fill={getScaleColor(entry.income, maxIncome, 'income')} />
                            ))}
                        </Bar>
                        <Bar
                            dataKey="expense"
                            name={t('chart.despesas')}
                            fill={SERIES_COLOR.expense}
                            radius={[4, 4, 0, 0]}
                            animationDuration={1500}
                            animationBegin={300}
                        >
                            {cashFlowData.map((entry, index) => (
                                <Cell key={index} fill={getScaleColor(entry.expense, maxExpense, 'expense')} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('common.semDados')}</div>
                )}
            </div>
        </div>
    );
}
